import { useEffect } from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { useLiveStream } from '../container';
import { liveStatus, setLiveStatus } from '../live';
import { keys } from '../query-keys';

/** How long events are gathered before one round of refetches, in milliseconds. */
export const LIVE_BATCH_MS = 250;

/**
 * Whether a cached query shows live work: what the polling covered, plus the
 * activity log and the print sheet, which a change makes stale. A photograph's
 * bytes never change under the same URL, so they are left alone.
 *
 * Doctors and patients are among it. The request of 6 October 2026 (second),
 * items 4 and 5: a record counts its cases, so every case that moves changes
 * a doctor's row, and the server announces a record added, renamed or deleted
 * as a change with no case to it, which reaches the lists the same way.
 */
export function showsLiveWork(queryKey: QueryKey): boolean {
  const [root, part] = queryKey;
  if (root === keys.kits.all[0]) return part !== 'photo-blob';
  return (
    root === keys.orders.all[0] ||
    root === keys.board()[0] ||
    root === 'activity' ||
    root === keys.printAll[0] ||
    root === keys.doctors.all[0] ||
    root === keys.patients.all[0]
  );
}

function refreshLiveWork(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ predicate: (query) => showsLiveWork(query.queryKey) });
}

/**
 * Keeps the open screens current from the live stream. Section 24 of the review
 * of 2 October 2026.
 *
 * Each event invalidates; nothing is patched by hand, so a missed event costs
 * a later refetch and never a wrong screen. Events arriving together (a hold
 * on an order of five trays) are gathered into one round. When the stream
 * comes back after a drop, everything live is refetched once, for whatever
 * was missed while it was down; when it drops, the same, so each screen
 * schedules its next poll at the faster pace straight away.
 *
 * Mounted once, in the signed in shell.
 */
export function useLiveUpdates(): void {
  const stream = useLiveStream();
  const queryClient = useQueryClient();

  useEffect(() => {
    let batch: ReturnType<typeof setTimeout> | null = null;
    let everOpen = false;

    const refreshSoon = () => {
      if (batch !== null) return;
      batch = setTimeout(() => {
        batch = null;
        refreshLiveWork(queryClient);
      }, LIVE_BATCH_MS);
    };

    const stop = stream.connect({
      onChanged: refreshSoon,
      onNotification: () => void queryClient.invalidateQueries({ queryKey: keys.notifications }),
      // A role, a supervisor flag or an exception changed: the menus and every
      // permission check on screen read the session, so it is read again.
      onSession: () => void queryClient.invalidateQueries({ queryKey: keys.session }),
      onStatus: (next) => {
        const was = liveStatus();
        setLiveStatus(next);
        if (next === 'open' && was !== 'open') {
          if (everOpen) {
            refreshSoon();
            void queryClient.invalidateQueries({ queryKey: keys.notifications });
          }
          everOpen = true;
        }
        if (next === 'down' && was === 'open') refreshSoon();
      },
    });

    return () => {
      stop();
      if (batch !== null) clearTimeout(batch);
      setLiveStatus('down');
    };
  }, [stream, queryClient]);
}
